import { EmailHint } from "@/components/EmailHint";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { AddressInput } from "@/components/AddressInput";
import { CATEGORY_SLUGS } from "@shared/constants/categories";
import { SECTION_SLUGS } from "@shared/constants/sections";
import { FIELD_KEYS } from "@shared/constants/fields";
import { evaluateShowIf, type ShowIfCondition } from "@shared/gate-eval";
import { PERSONAL_TEMPLATE } from "@shared/interview/board-template";
import type { GenericEntry, GenericField } from "@shared/schema";
import { useSectionFields } from "./useSectionFields";
import { saveInBackground, useSaveFailure } from "./saveFailure";
import { CELL_BOX, DateCell, IconTextCell, PickerCell, RemoveButton } from "./InterviewCells";
import { HomeTenureQuestion } from "./HomeTenureQuestion";
import { useHome } from "./ch2/useHome";
import { StillHereLine } from "./ch2/Ch2Engine";

/**
 * s223 / s233 — PERSONAL DETAILS (SPEC: `Main.dc.html` v443, the `you` step, drawn by the template's `view.isFields`
 * block — not by `steps()`, so its words are held in `PERSONAL_TEMPLATE` and checked against the committed board by
 * `board-template-copy.test.ts`).
 *
 * s233 (D-233.68, D-233.75): the old "Your name and email" and "Details forms ask for" screens are ONE screen, in
 * the board's order — the name, the home address, the birth details, the mobile and the phone, the email. The hints
 * are gone (rule 146), and so is the Province box: the home address carries the province. `clients.jurisdiction_
 * province` keeps its other writers (signup, Settings) and its readers (PROVINCE-FROM-ADDRESS-1, registered).
 *
 * Where each answer lives: About You's own fields, on the card row — the plan's "you" person — through
 * `/typed-rows` (D-229.68(b)). 🔴 EMAIL IS READ-ONLY: it is the verified signup address (`users.email`), and nothing
 * in the product can change it (`ACCOUNT-EMAIL-CHANGE-1`).
 *
 * 🔴 "Province or territory of birth" appears only when the country of birth is Canada. That rule is the FIELD's own
 * `showIf` (about-you.ts), evaluated by the single evaluator `evaluateShowIf` — never an inline check here.
 */

/** The seven birth boxes, in the board's order (row one: birth facts; row two: names and facts forms check). */
const BIRTH = [
  FIELD_KEYS.DATE_OF_BIRTH, FIELD_KEYS.PLACE_OF_BIRTH, FIELD_KEYS.COUNTRY_OF_BIRTH, FIELD_KEYS.PROVINCE_OF_BIRTH,
  FIELD_KEYS.NAME_AT_BIRTH, FIELD_KEYS.MOTHER_MAIDEN_NAME, FIELD_KEYS.LAST_OCCUPATION,
] as const;

/** The card's current values, field key → value. Pure. (Moved here from the old facts screen, s233.) */
export function cardValues(
  entries: readonly Pick<GenericEntry, "sectionId" | "fieldId" | "rowId" | "value">[],
  sectionId: string | undefined,
  keyByFieldId: ReadonlyMap<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const e of entries) {
    if (e.sectionId !== sectionId || e.rowId) continue;
    const key = keyByFieldId.get(e.fieldId);
    if (key) out[key] = e.value ?? "";
  }
  return out;
}

/** Is a field shown, by its own `showIf`, given the card's values? Pure; the single evaluator decides. */
export function isShown(field: Pick<GenericField, "conditions"> | undefined, byFieldId: Record<string, { value: string }>): boolean {
  return evaluateShowIf((field?.conditions ?? null) as ShowIfCondition | null, byFieldId);
}

/** The other addresses as stored (a JSON array of `{ email }`), in order, empties dropped. Pure. */
export function parseOtherEmails(stored: string | undefined): string[] {
  try {
    const parsed = JSON.parse(stored || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.map((r) => (r && typeof r === "object" ? String((r as { email?: unknown }).email ?? "") : "")).filter((s) => s.trim() !== "");
  } catch {
    return [];
  }
}

/** The boxes' values as the stored array — the `repeating_group` shape `RepeatingGroupInput` reads. Pure. */
export function serializeOtherEmails(boxes: readonly string[]): string {
  return JSON.stringify(boxes.map((s) => s.trim()).filter(Boolean).map((email) => ({ email })));
}

const T = PERSONAL_TEMPLATE;

/** One other-email box: its own id (its key) and what it holds. */
type EmailBox = { id: number; v: string };

export function YouScreen({ clientId, onAnswered }: { clientId: string; onAnswered: () => void }) {
  const { categoryId, categoryType, sectionId, fieldIdByKey, fields } = useSectionFields(
    clientId,
    CATEGORY_SLUGS.ABOUT_YOU,
    SECTION_SLUGS.ABOUT_YOU,
  );
  const keyByFieldId = useMemo(() => {
    const m = new Map<string, string>();
    fieldIdByKey.forEach((id, key) => m.set(id, key));
    return m;
  }, [fieldIdByKey]);

  const { data: entries } = useQuery<GenericEntry[]>({
    queryKey: ["/api/clients", clientId, "generic-entries", categoryType],
    queryFn: async () => (await apiRequest("GET", `/api/clients/${clientId}/generic-entries?categoryType=${categoryType}`)).json(),
    enabled: !!clientId && !!categoryType,
  });
  const values = useMemo(() => cardValues(entries ?? [], sectionId, keyByFieldId), [entries, sectionId, keyByFieldId]);
  const byFieldId = useMemo(() => {
    const m: Record<string, { value: string }> = {};
    for (const [key, v] of Object.entries(values)) { const id = fieldIdByKey.get(key); if (id) m[id] = { value: v }; }
    return m;
  }, [values, fieldIdByKey]);
  const fieldByKey = useMemo(() => new Map(fields.map((f) => [f.fieldKey, f])), [fields]);

  // The signed-in account's email, from the cache AuthContext already keeps (`/api/auth/me`).
  const { data: me } = useQuery<{ email?: string }>({ queryKey: ["/api/auth/me"] });

  // s228 (INTERVIEW-SAVE-FAILURE): a refused save puts up the red pop-up (saveFailure.ts). The writers below still
  // throw, so a sequence stops at the first refusal and the box keeps what was typed.
  const onSaveFailed = useSaveFailure();
  const upsertMutation = useMutation({
    // s229 (D-229.68(b), D-229.94): About You is the plan's "you" person — its card saves as that typed record
    // (row id null: a singleton), with its row-exists cells, through /typed-rows.
    mutationFn: async ({ categoryId, sectionId, fieldId, value }: { categoryId: string; sectionId: string; fieldId: string; value: string }) =>
      await apiRequest("POST", `/api/clients/${clientId}/typed-rows`, { categoryId, sectionId, rowId: null, cells: [{ fieldId, value }] }),
    onError: onSaveFailed,
  });

  /** Write one About You field on the card row. Last-wins; the server upserts on (client, field, row). */
  const write = async (key: string, value: string) => {
    const fieldId = fieldIdByKey.get(key);
    if (!fieldId || !sectionId || !categoryId) return;
    await upsertMutation.mutateAsync({ categoryId, sectionId, fieldId, value });
    void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries"] });
    onAnswered();
  };

  // The address box is controlled: its draft lives here while it is typed, and a ref carries the latest draft to the
  // blur (AddressInput defers its own blur a tick after a pick, so the ref is always current).
  const [addressDraft, setAddressDraft] = useState<string | null>(null);
  const pendingAddress = useRef<string | null>(null);
  const storedAddress = values[FIELD_KEYS.HOME_ADDRESS] ?? "";
  // s237 PR 5 (part C, D-235.65): a new address while the home row holds another asks first — the save waits for the answer.
  const home = useHome(clientId, onAnswered, { ready: entries !== undefined && fieldIdByKey.size > 0, values, write });
  const [moving, setMoving] = useState<{ address: string; question: string } | null>(null);
  const commitAddress = async () => {
    const v = pendingAddress.current;
    if (v === null || v === storedAddress) return;
    const ask = home.addressChange(v);
    if (ask) { setMoving({ address: v, question: ask.question }); return; }
    await write(FIELD_KEYS.HOME_ADDRESS, v);
    pendingAddress.current = null;
    setAddressDraft(null);
  };
  const answerMove = async (choice: "keep" | "remove") => {
    if (!moving) return;
    await home.moveHome(moving.address, choice);
    setMoving(null);
    pendingAddress.current = null;
    setAddressDraft(null);
  };

  // The other addresses: the boxes hold their own drafts; a blur writes the whole list. "Add another" adds an
  // empty box here only — nothing is stored until something is typed in it.
  // s235 (Reade, walk item 9): a box can be removed (its ×, once there is more than one box). Each box carries its OWN
  // id as its key, never its position: the boxes are uncontrolled, so a position key let the box after a removed one
  // keep the removed one's text. Once the planner has touched the list, the list on screen is theirs — a save that
  // comes back does not redraw it (that would move the cursor out of the box they tabbed into).
  const storedEmails = values[FIELD_KEYS.OTHER_EMAILS];
  const nextBoxId = useRef(0);
  const toBoxes = (vs: readonly string[]): EmailBox[] => vs.map((v) => ({ id: nextBoxId.current++, v }));
  const storedBoxes = useMemo(() => { const s = parseOtherEmails(storedEmails); return toBoxes(s.length ? s : [""]); }, [storedEmails]);
  const [boxes, setBoxes] = useState<EmailBox[] | null>(null);
  const shownBoxes = boxes ?? storedBoxes;
  const commitEmails = (next: readonly EmailBox[]) => {
    const v = serializeOtherEmails(next.map((b) => b.v));
    if (v === serializeOtherEmails(parseOtherEmails(storedEmails))) return;
    saveInBackground(write(FIELD_KEYS.OTHER_EMAILS, v));
  };
  const removeEmail = (i: number) => {
    const left = shownBoxes.filter((_, j) => j !== i);
    // s235 (D-235.56): removing the last address leaves one empty box, as the screen opens with none stored.
    const next = left.length ? left : toBoxes([""]);
    setBoxes(next);
    commitEmails(next);
  };

  const ready = !!sectionId && !!categoryId && entries !== undefined && fieldIdByKey.size > 0;
  if (!ready) return <div data-testid="interview-you-loading" />;

  const tid = (what: string) => `interview-you-${what}`;
  const has = (key: string) => fieldIdByKey.has(key);
  const text = (key: string, label: string, opts: { phone?: boolean; placeholder?: string } = {}) => (
    <IconTextCell value={values[key] ?? ""} ariaLabel={label} placeholder={opts.placeholder}
      onCommit={(v) => saveInBackground(write(key, v))} testId={tid(key)} phone={opts.phone} />
  );
  const field = (key: string, label: string, control: ReactNode) => (
    <div key={key} className="flex flex-col gap-[7px]" data-testid={`${tid(key)}-field`}>
      <label className="text-[13px] text-warm-charcoal">{label}</label>
      {control}
    </div>
  );
  const birthBox = (key: (typeof BIRTH)[number]) => {
    const copy = T[key];
    const v = values[key] ?? "";
    const placeholder = "placeholder" in copy ? copy.placeholder : undefined;
    if (key === FIELD_KEYS.DATE_OF_BIRTH) {
      return <DateCell value={v} ariaLabel={copy.label} onCommit={(d) => saveInBackground(write(key, d))} testId={tid(key)} />;
    }
    if (key === FIELD_KEYS.PROVINCE_OF_BIRTH) {
      const choices = ((fieldByKey.get(key)?.options as { choices?: string[] } | null)?.choices ?? []);
      return <PickerCell value={v} options={choices} placeholder={placeholder} ariaLabel={copy.label}
        onCommit={(c) => saveInBackground(write(key, c))} testId={tid(key)} />;
    }
    return text(key, copy.label, { placeholder });
  };

  return (
    <div className="flex max-w-[980px] flex-col gap-[22px]" data-testid="interview-you">
      {/* The name. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {([FIELD_KEYS.FIRST_NAME, FIELD_KEYS.MIDDLE_NAME, FIELD_KEYS.FAMILY_NAME] as const).filter(has).map((key) =>
          field(key, T[key].label, text(key, T[key].label)))}
      </div>

      {/* The home address — the product's one address control. It commits the whole address on blur of any box.
          s235 (D-235.97, superseding D-235.26's six open boxes): ONE line — typing offers addresses, a pick fills the six
          parts, the line reads the whole address; the six open on Edit with Done (AddressInput's interviewLook="line"). */}
      {has(FIELD_KEYS.HOME_ADDRESS) && field(FIELD_KEYS.HOME_ADDRESS, T.home_address.label, (
        <AddressInput
          value={addressDraft ?? storedAddress}
          fieldId={fieldIdByKey.get(FIELD_KEYS.HOME_ADDRESS)!}
          onChange={(v) => { pendingAddress.current = v; setAddressDraft(v); }}
          onBlur={() => saveInBackground(commitAddress())}
          interviewLook="line"
          linePlaceholder={T.home_address.placeholder}
        />
      ))}
      {moving && (
        <StillHereLine question={moving.question} testId={tid("home-move")}
          onKeep={() => saveInBackground(answerMove("keep"))} onRemove={() => saveInBackground(answerMove("remove"))} />
      )}

      {/* s237 (D-236.47(1), D-236.88-6): the home's tenure, asked once, under the address it describes. */}
      {has(FIELD_KEYS.HOME_TENURE) && <HomeTenureQuestion clientId={clientId} onAnswered={onAnswered} />}

      {/* The birth details, and the names and facts forms check: two rows of three. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {BIRTH.filter((key) => has(key) && isShown(fieldByKey.get(key), byFieldId)).map((key) => field(key, T[key].label, birthBox(key)))}
      </div>

      {/* The mobile and the phone's make and model (rule 42 carries the phone into the protected step). */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {/* s228 (item 69): the product's one phone convention, as the contact card's phone box. */}
        {has(FIELD_KEYS.MOBILE) && field(FIELD_KEYS.MOBILE, T.mobile.label, text(FIELD_KEYS.MOBILE, T.mobile.label, { phone: true }))}
        {has(FIELD_KEYS.PHONE_MAKE_MODEL) && field(FIELD_KEYS.PHONE_MAKE_MODEL, T.phone_make_model.label, text(FIELD_KEYS.PHONE_MAKE_MODEL, T.phone_make_model.label, { placeholder: T.phone_make_model.placeholder }))}
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-[7px]" data-testid={`${tid("email")}-field`}>
          <label className="text-[13px] text-warm-charcoal">{T.email.label}</label>
          {/* Read-only: a box that shows the address, never an input (D-222.8). It GROWS with its address (s223, the
              first defect the widened rule-133 detector caught): `h-11` is its floor, not its height. */}
          <div className={`${CELL_BOX} flex !h-auto min-h-11 max-w-[340px] items-center break-words px-[15px] py-2`} data-testid={tid("email")}>
            {me?.email ?? ""}
          </div>
        </div>

        {has(FIELD_KEYS.OTHER_EMAILS) && (
          <div className="flex flex-col gap-[7px]" data-testid={`${tid(FIELD_KEYS.OTHER_EMAILS)}-field`}>
            <label className="text-[13px] text-warm-charcoal">{T.other_emails.label}</label>
            <div className="mt-0.5 flex flex-col gap-2.5">
              {shownBoxes.map((b, i) => (
                <div key={b.id} className="flex items-center gap-1">
                  <div className="w-full max-w-[340px]">
                    <input
                      className={`${CELL_BOX} px-[15px]`}
                      type="email"
                      inputMode="email"
                      defaultValue={b.v}
                      placeholder={T.other_emails.placeholder}
                      aria-label={`${T.other_emails.label} ${i + 1}`}
                      // s235 (D-235.56): Chrome filled this box with an address it remembered and painted it light blue
                      // (Reade's walk of #193). The interview asks it not to; the interview's autofill rule (index.css)
                      // keeps the box's own fill if it does anyway.
                      autoComplete="off"
                      onChange={(e) => { const v = e.target.value; setBoxes(shownBoxes.map((x, j) => (j === i ? { ...x, v } : x))); }}
                      onBlur={(e) => {
                        const next = shownBoxes.map((x, j) => (j === i ? { ...x, v: e.target.value } : x));
                        setBoxes(next);
                        commitEmails(next);
                      }}
                      data-testid={`${tid("other-email")}-${i}`}
                    />
                    {/* s236 (EMAIL-FORMAT-1): the quiet line under the box; the address saves as typed. */}
                    <EmailHint value={b.v} className="text-[12px] text-warm-muted" testId={`${tid("other-email")}-${i}-hint`} />
                  </div>
                  {/* s235 (D-235.56, Reade's walk of #193): a box that holds an address always has its ×; an empty box
                      has one only when there are two or more boxes. */}
                  {(b.v.trim() !== "" || shownBoxes.length > 1) && (
                    <RemoveButton onClick={() => removeEmail(i)} label={`Remove ${T.other_emails.label} ${i + 1}`} title="Remove this email" testId={`${tid("remove-email")}-${i}`} />
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setBoxes([...shownBoxes, ...toBoxes([""])])}
              className="mt-1 inline-flex h-11 items-center gap-[7px] self-start text-[13px] text-sage-600 hover:text-sage-800"
              data-testid={tid("add-email")}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              {T.other_emails.add}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
